import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { authService } from '@/services/auth'
import { OrganizationType } from '@/types/organization'

interface User {
  id?: number
  name?: string
  email: string
  avatar?: string
  first_name?: string
  last_name?: string
  organizations?: OrganizationType[]
}

interface AuthContextType {
  user: User | null
  login: (email: string, password: string) => Promise<boolean>
  logout: () => void
  refreshSession: () => Promise<boolean>
  isSessionValid: boolean
  validationInProgress: boolean
  loading: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isSessionValid, setIsSessionValid] = useState(true)
  const [validationInProgress, setValidationInProgress] = useState(false)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  
  // Request deduplication for token validation
  let validationPromise: Promise<any> | null = null

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const resp = await authService.signin(email, password)
      
      // Check for token in different places
      const token = resp.data?.token || resp.data?.user?.token
      const userData = resp.data?.user || resp.data
      
      if (token && userData) {
        // Save token without Bearer prefix - the API interceptor will add it
        localStorage.setItem('authToken', token)
        
        // Create user object
        const user: User = {
          id: userData.id,
          email: userData.email,
          name: userData.first_name && userData.last_name 
            ? `${userData.first_name} ${userData.last_name}`
            : userData.email.split('@')[0],
          first_name: userData.first_name,
          last_name: userData.last_name,
          avatar: undefined // No default avatar - will use initials fallback
        }
        
        setUser(user)
        localStorage.setItem('user', JSON.stringify(user))
        setIsSessionValid(true)
        return true
      }
      
      return false
    } catch (error) {
      console.error('Login error:', error)
      setIsSessionValid(false)
      return false
    }
  }

  // Deduplicated token validation function
  const validateTokenWithDeduplication = async (token: string) => {
    if (validationInProgress && validationPromise) {
      console.log('Token validation already in progress, waiting for existing request...')
      return await validationPromise
    }
    
    // Validating authentication token
    setValidationInProgress(true)
    validationPromise = authService.validateToken(token)
    
    try {
      const result = await validationPromise
      return result
    } finally {
      setValidationInProgress(false)
      validationPromise = null
    }
  }

  const refreshSession = async (): Promise<boolean> => {
    // Don't refresh in popup windows to avoid OAuth interference
    const isPopup = window.opener !== null
    if (isPopup) {
      console.log('Skipping session refresh in OAuth popup window')
      return true
    }

    const authToken = localStorage.getItem('authToken')
    if (!authToken) {
      setIsSessionValid(false)
      return false
    }

    try {
      const validUser = await validateTokenWithDeduplication(authToken)
      if (validUser) {
        const newUser: User = {
          id: validUser.id,
          email: validUser.email,
          name: validUser.first_name && validUser.last_name 
            ? `${validUser.first_name} ${validUser.last_name}`
            : validUser.email.split('@')[0],
          first_name: validUser.first_name,
          last_name: validUser.last_name,
          avatar: undefined
        }
        
        // Only update state if user data has actually changed
        const currentUserStr = JSON.stringify(user)
        const newUserStr = JSON.stringify(newUser)
        if (currentUserStr !== newUserStr) {
          setUser(newUser)
          localStorage.setItem('user', JSON.stringify(newUser))
        }
        // Only update session validity if it changed
        if (!isSessionValid) {
          setIsSessionValid(true)
        }
        return true
      } else {
        setIsSessionValid(false)
        return false
      }
    } catch (error) {
      console.error('Session refresh error:', error)
      setIsSessionValid(false)
      return false
    }
  }

  const logout = async () => {
    try {
      await authService.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      setUser(null)
      localStorage.removeItem('authToken')
      localStorage.removeItem('user')
      setIsSessionValid(false)
      // Don't remove activeOrganization - we want to remember it
      navigate('/login')
    }
  }

  // Check for existing session on mount
  useEffect(() => {
    // Don't run auth validation in popup windows to prevent session interference
    const isPopup = window.opener !== null
    if (isPopup) {
      console.log('Skipping auth validation in popup window to prevent session interference')
      setLoading(false)
      return
    }
    
    // Don't trigger session validation during setup flow
    const currentPath = window.location.pathname
    if (currentPath.startsWith('/get-started/')) {
      console.log('Skipping auth validation during setup flow')
      // Still check if there's a token, but don't validate it
      const authToken = localStorage.getItem('authToken')
      const storedUser = localStorage.getItem('user')
      if (authToken && storedUser) {
        const user = JSON.parse(storedUser)
        setUser(user)
        setIsSessionValid(true)
      }
      setLoading(false)
      return
    }
    
    const initAuth = async () => {
      const authToken = localStorage.getItem('authToken')
      const storedUser = localStorage.getItem('user')
      
      if (authToken && storedUser) {
        try {
          // Validate token with backend using deduplicated function
          const validUser = await validateTokenWithDeduplication(authToken)
          if (validUser) {
            // Update user data from server response
            const newUser: User = {
              id: validUser.id,
              email: validUser.email,
              name: validUser.first_name && validUser.last_name 
                ? `${validUser.first_name} ${validUser.last_name}`
                : validUser.email.split('@')[0],
              first_name: validUser.first_name,
              last_name: validUser.last_name,
              avatar: undefined
            }
            
            // Only update state if user data has actually changed
            const currentUserStr = JSON.stringify(user)
            const newUserStr = JSON.stringify(newUser)
            if (currentUserStr !== newUserStr) {
              setUser(newUser)
              localStorage.setItem('user', JSON.stringify(newUser))
            }
            setIsSessionValid(true)
          } else {
            // Invalid token, clear storage
            localStorage.removeItem('authToken')
            localStorage.removeItem('user')
            setIsSessionValid(false)
          }
        } catch (error) {
          console.error('Token validation error:', error)
          // Clear invalid session
          localStorage.removeItem('authToken')
          localStorage.removeItem('user')
          setIsSessionValid(false)
        }
      } else {
        // No token or user data, not authenticated
        setIsSessionValid(false)
      }
      
      // Always set loading to false after initialization
      setLoading(false)
    }
    
    initAuth()
  }, [])

  // Note: Removed automatic inactivity-based session refresh
  // Pages now refresh naturally on user activity (navigation, interactions)
  // OAuth popup windows remain protected (refreshSession has built-in popup detection)

  // Listen for OAuth completion messages from popup windows
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Only accept messages from same origin for security
      if (event.origin !== window.location.origin) {
        return
      }

      if (event.data.type === 'OAUTH_SUCCESS') {
        console.log('OAuth success received from popup:', event.data)
        // No need to refresh session since auth is already handled by main window
        // Just dispatch a custom event that components can listen to
        window.dispatchEvent(new CustomEvent('oauth-completed', { 
          detail: event.data.data 
        }))
      } else if (event.data.type === 'OAUTH_ERROR') {
        console.log('OAuth error received from popup:', event.data)
        // Dispatch error event for components to handle
        window.dispatchEvent(new CustomEvent('oauth-error', { 
          detail: event.data.data 
        }))
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  // Centralized 401 error handling from API interceptor
  useEffect(() => {
    const handleAuthError = async (event: any) => {
      const { status, url } = event.detail
      
      if (status === 401) {
        console.log('Centralized 401 error handler triggered for URL:', url)
        
        // Try to refresh session once
        const refreshed = await refreshSession()
        if (!refreshed) {
          console.log('Session refresh failed - invalidating session')
          setIsSessionValid(false)
          setUser(null)
          // The protected route will handle the redirect to login
        }
      }
    }

    window.addEventListener('auth-error', handleAuthError)
    return () => window.removeEventListener('auth-error', handleAuthError)
  }, [refreshSession])

  return (
    <AuthContext.Provider value={{ user, login, logout, refreshSession, isSessionValid, validationInProgress, loading }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}